import { ErrorMessage, FieldAttributes } from 'formik'
import NumberFormat, { NumberFormatProps } from 'react-number-format'

/** Useful Form utilities for Formik */
export function useForms() {
  /**
   * Tailwind + DaisyUI default input classNames
   * import and override/extend them locally.
   * @example
   * const myComponentClasses = classNames(defaultInputClasses, {
   *   'text-red-500': true
   * })
   */
  const defaultInputClasses = `
    input input-sm input-bordered
    placeholder:disabled:invisible
    disabled:text-gray-700 disabled:font-semibold
  `

  /**
   * Tailwind + DaisyUI default select classNames
   * @see defaultInputClasses
   */
  const defaultSelectClasses = `
    select select-sm select-bordered leading-none
    disabled:text-gray-700 disabled:font-semibold
  `

  const defaultNumericInputProps = {
    as: NumberFormat,
    className: defaultInputClasses,
  }

  /**
   * When used inside a Formik Form, use these as default props
   * override them as needed instead of creating "Universal Fields"
   * @example
   * <Field
   *   name="amount"
   *   {...defaultCurrencyInputProps}
   * />
   */
  const defaultCurrencyInputProps: NumberFormatProps | FieldAttributes<any> = {
    ...defaultNumericInputProps,
    prefix: '$',
    thousandSeparator: true,
    decimalScale: 2,
  }

  /**
   * Formik + NumberFormat has a quirk
   * and needs a setTimeout to trigger the value change
   * @param {Object} param0
   * @param {Function} param0.setFieldValue - Formik setFieldValue function
   * @param {string} param0.fieldName - Field name
   * @param {number} param0.value - Value to set
   * @example
   * <Field
   *   name="amount"
   *   as={NumberFormat}
   *   onValueChange={(values) => {
   *    setNumericValue({
   *     setFieldValue,
   *     fieldName: 'amount',
   *     value: values.floatValue,
   *  })
   * />
   */
  const setNumericValue = ({ setFieldValue, fieldName, value }) => {
    setTimeout(() => {
      if (value == null) {
        return setFieldValue(fieldName, null)
      }
      return setFieldValue(fieldName, value)
    }, 0)
  }

  /**
   * Nullify empty strings prior to submit to backend
   *
   * @description Sometimes the backend expects null values instead of empty strings
   * @param {Object} values - Formik Form values
   */
  function nullifyEmptyStrings(values: Record<string, any>) {
    if (!values || typeof values !== 'object') {
      return values
    }

    if (Array.isArray(values)) {
      return values.map((item) => nullifyEmptyStrings(item))
    }

    const result = { ...values }
    for (const key in result) {
      if (result[key] === '') {
        result[key] = null
      } else if (typeof result[key] === 'object' && result[key] !== null) {
        result[key] = nullifyEmptyStrings(result[key])
      }
    }
    return result
  }

  /**
   * Remove null values from an object
   * @param {Object} obj - Object to remove null values from
   * @returns {Object} Object with null values removed
   */
  function removeNullValuesFromObject(obj: Record<string, any>) {
    return Object.fromEntries(Object.entries(obj).filter(([_, value]) => value !== null))
  }

  const CustomErrorMessage = ({ name }) => {
    return (
      <ErrorMessage
        name={name}
        render={(msg) => <span className='text-error text-xs'>{msg}</span>}
      />
    )
  }

  return {
    defaultInputClasses,
    defaultSelectClasses,
    defaultNumericInputProps,
    defaultCurrencyInputProps,
    setNumericValue,
    nullifyEmptyStrings,
    removeNullValuesFromObject,
    CustomErrorMessage,
  }
}
